/* eslint-disable react/jsx-no-useless-fragment */
import { CategoryItemsProps, Template } from '@zeus/shared';
import { BoxComponent } from '../../../../../base/box/Box';
import { TypographyComponent } from '../../../../../base/typography/Typography';
import { memo, useEffect, useState } from 'react';
import { getScaleSize, isBetweenRange } from '../../../../../utils/utils';
import { useNormalizedPixels } from '../../../../../hooks/useResize';

interface Item extends CategoryItemsProps {
  hasValidModifiers: boolean;
}

interface ItemProps {
  template: Template;
  item: Item;
  development?: boolean;
  lastItemIndex: number;
  index: number;
  timezone?: string;
  scale?: number;
  hasAnyModifier: boolean;
  tenantId?: number;
}

const Item = ({ template, item, development, timezone, scale = 1, index }: ItemProps) => {
  const [active, setActive] = useState<boolean | null>(true);
  const { normalizePixels } = useNormalizedPixels();

  useEffect(() => {
    if (development === false) {
      const intervalId = setInterval(() => {
        setActive(isBetweenRange(item.schedules, timezone));
      }, 10000);

      return () => clearInterval(intervalId);
    }
  }, []);

  const isItemPriceEnabled = template?.itemPrice && template.itemPrice.isEnabled;
  const isDevelopment = development ? true : isBetweenRange(item.schedules, timezone);
  const isDescription = template?.itemDescription?.isEnabled;
  const itemTextScale = template?.item?.textScale ?? 1;
  const fontFamily = template?.fontFamilyName;

  return (
    active && (
      <>
        {isDevelopment && (
          <BoxComponent
            key={item.id}
            sx={{
              backgroundColor:
                (index + 1) % 2 === 0 ? template?.evenRowsBackgroundColor : template?.oddRowsBackgroundColor,
              borderRadius: 0,
              textTransform: 'capitalize',
              flex: 1,
              minWidth: 0,
              height: '100%',
              display: 'flex',
              flexDirection: index % 2 === 0 ? 'row' : 'row-reverse',
              overflow: template.itemImages?.overflowImages ? 'visible' : 'hidden',
              position: 'relative',
              width: '100%',
              padding: '0px 3%',
              // ...(item.outOfStock ? { opacity: '30%', zIndex: 1 } : {}),
            }}
          >
            {template.itemImages?.isEnabled && (
              <BoxComponent
                sx={{
                  width: '20%',
                  position: 'relative',
                  height: template.itemImages?.overflowImages
                    ? `calc(100% + ${normalizePixels(24, 'height')}px)`
                    : '90%',
                  margin: template.itemImages?.overflowImages ? `${normalizePixels(-12, 'height')}px 0px` : 'auto 0px',
                  overflow: 'visible',
                  zIndex: template.itemImages?.overflowImages ? 1 : 'auto',
                }}
              >
                <BoxComponent>
                  <img
                    src={item.itemPicture}
                    alt={item.itemName}
                    style={{
                      width: '100%',
                      height: '100%',
                      position: 'absolute',
                      inset: 0,
                      zIndex: 1,
                      ...(item.outOfStock ? { opacity: '30%', zIndex: 1 } : {}),
                    }}
                  />
                </BoxComponent>

                {item.outOfStock && (
                  <BoxComponent
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'inline-block',
                      backgroundColor: '#B76557',
                      color: '#fff',
                      textAlign: 'center',
                      borderRadius: `${normalizePixels(4, 'height')}px`,
                      padding: `${normalizePixels(3, 'height')}px ${normalizePixels(8, 'width')}px`,
                      fontSize: getScaleSize(normalizePixels(12.5, 'height'), scale),
                      zIndex: 2,
                    }}
                  >
                    Out of stock
                  </BoxComponent>
                )}
              </BoxComponent>
            )}

            <BoxComponent
              sx={{
                width: '60%',
                display: 'flex',
                flexDirection: 'column',
                rowGap: normalizePixels(1, 'width'),
                justifyContent: 'center',
                padding: normalizePixels(3, 'width'),
                ...(item.outOfStock ? { opacity: '30%', zIndex: 1 } : {}),
              }}
            >
              <BoxComponent
                sx={{
                  display: 'flex',
                  flexDirection: index % 2 === 0 ? 'row' : 'row-reverse',
                  columnGap: normalizePixels(2, 'width'),
                  alignItems: 'center',
                }}
              >
                <TypographyComponent
                  sx={{
                    ...style.itemName,
                    color: template.item.itemName.color ? template.item.itemName.color : '#242528',
                    minWidth: 0,
                    fontSize: getScaleSize(normalizePixels(32 * itemTextScale, 'height'), scale),
                    // ...(item.outOfStock ? { opacity: '50%' } : {}),
                    fontFamily: fontFamily,
                    fontWeight: template.item.itemName.fontWeight,
                    fontStyle: template.item.itemName.fontStyle,
                    letterSpacing: template.item.itemName.letterSpacing,
                    //   lineHeight: template.item.itemName.lineHeight,
                  }}
                >
                  {item.itemName}
                </TypographyComponent>
                {isItemPriceEnabled && (
                  <TypographyComponent
                    sx={{
                      ...style.itemPrice,
                      color: template?.item?.itemPrice?.color ? template?.item?.itemPrice?.color : '#B76557',
                      fontSize: getScaleSize(normalizePixels(32 * itemTextScale, 'height'), scale),
                      fontFamily: fontFamily,
                      fontWeight: template.item.itemPrice?.fontWeight,
                      fontStyle: template.item.itemPrice?.fontStyle,
                      letterSpacing: template.item.itemPrice?.letterSpacing,
                      // lineHeight: template.item.itemPrice?.lineHeight,
                    }}
                  >
                    $ {Number(item.itemPrice).toFixed(2)}
                  </TypographyComponent>
                )}
              </BoxComponent>
              {isDescription && item.itemDescription && (
                <TypographyComponent
                  sx={{
                    ...style.itemDesc,
                    color: template.item?.itemDescription?.color ? template.item?.itemDescription?.color : '#717171',
                    fontSize: getScaleSize(normalizePixels(18 * itemTextScale, 'height'), scale),
                    ...(item.outOfStock ? { opacity: '50%' } : {}),
                    fontFamily: fontFamily,
                    fontWeight: template.item.itemDescription?.fontWeight,
                    fontStyle: template.item.itemDescription?.fontStyle,
                    letterSpacing: template.item.itemDescription?.letterSpacing,
                    // lineHeight: template.item.itemDescription?.lineHeight,
                    direction: index % 2 === 0 ? 'ltr' : 'rtl',
                    //textAlign: index % 2 === 0 ? 'left' : 'right',
                  }}
                >
                  {item.itemDescription}
                </TypographyComponent>
              )}
            </BoxComponent>
            {/* Item details - fixed at bottom */}
          </BoxComponent>
        )}
      </>
    )
  );
};

export default memo(Item);

const style = {
  container: {
    alignSelf: 'start',
    height: 'min-content',
    overflow: 'hidden',
  },
  itemNameContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  itemName: {
    fontWeight: '600',
    fontFamily: 'Manrope, sans-serif',
    '-webkit-line-clamp': '1',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemPrice: {
    textAlign: 'center' as const,
    fontFamily: 'Manrope, sans-serif',
    lineHeight: 'normal',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  itemDesc: {
    fontWeight: '400',
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    // whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
};
